import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/race-tracker/components/ui/button";
import { Input } from "@/race-tracker/components/ui/input";
import { Label } from "@/race-tracker/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/race-tracker/components/ui/select";
import { Loader2, Save } from "lucide-react";
import { SerperColumnFieldGrid } from "@/race-tracker/components/sweep/SerperColumnFieldGrid";
import {
  normalizeSerperQueryFields,
  normalizeSerperMode,
  buildSerperQueryPreview,
  effectiveSerperGlHl,
} from "@/race-tracker/lib/serperQueryCatalog";

export default function SerperQuerySetupTab({
  race,
  advertiserName = "",
  settingsHref = "/admin/integrations",
  sweepConfig = null,
  sweepConfigLoading = false,
  isSuperAdmin = false,
  onSaveSerperRaceQuery,
  onSaveSerperLocale,
}) {
  const [draftFields, setDraftFields] = useState(() => normalizeSerperQueryFields(race?.serper_query_fields));
  const [draftMode, setDraftMode] = useState(() => normalizeSerperMode(race?.serper_mode));
  const [savingRace, setSavingRace] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [orgErr, setOrgErr] = useState("");
  const [raceErr, setRaceErr] = useState("");
  const [orgGl, setOrgGl] = useState("");
  const [orgHl, setOrgHl] = useState("");
  const [orgTbs, setOrgTbs] = useState("");

  useEffect(() => {
    setDraftFields(normalizeSerperQueryFields(race?.serper_query_fields));
    setDraftMode(normalizeSerperMode(race?.serper_mode));
  }, [race?.id, race?.serper_query_fields, race?.serper_mode]);

  useEffect(() => {
    if (!sweepConfig) return;
    setOrgGl(sweepConfig.serper_gl != null ? String(sweepConfig.serper_gl) : "");
    setOrgHl(sweepConfig.serper_hl != null ? String(sweepConfig.serper_hl) : "");
    setOrgTbs(sweepConfig.serper_tbs != null ? String(sweepConfig.serper_tbs) : "");
  }, [sweepConfig]);

  const previewQ = useMemo(
    () => buildSerperQueryPreview({ ...race, serper_query_fields: draftFields }, advertiserName),
    [race, draftFields, advertiserName]
  );

  const { gl: effGl, hl: effHl } = effectiveSerperGlHl(orgGl, orgHl);
  const effTbs = (orgTbs || "").trim();

  const saveRaceSerper = async () => {
    if (!race?.id || !onSaveSerperRaceQuery) return;
    setRaceErr("");
    setSavingRace(true);
    try {
      await onSaveSerperRaceQuery({
        serper_query_fields: normalizeSerperQueryFields(draftFields),
        serper_mode: normalizeSerperMode(draftMode),
      });
    } catch (e) {
      setRaceErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingRace(false);
    }
  };

  const saveOrgLocale = async () => {
    if (!onSaveSerperLocale || !isSuperAdmin) return;
    setOrgErr("");
    setSavingOrg(true);
    try {
      await onSaveSerperLocale({
        serper_gl: orgGl.trim(),
        serper_hl: orgHl.trim(),
        serper_tbs: orgTbs.trim(),
      });
    } catch (e) {
      setOrgErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingOrg(false);
    }
  };

  const normalizedRaceFields = normalizeSerperQueryFields(race?.serper_query_fields);
  const normalizedRaceMode = normalizeSerperMode(race?.serper_mode);
  const fieldsDirty = JSON.stringify(draftFields) !== JSON.stringify(normalizedRaceFields);
  const modeDirty = normalizeSerperMode(draftMode) !== normalizedRaceMode;
  const raceDirty = fieldsDirty || modeDirty;

  const modeLabel = draftMode === "search" ? "Web search" : "News";

  return (
    <div className="px-4 py-4 space-y-6 max-w-3xl">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        <strong className="text-foreground">Per race:</strong> Serper endpoint (news vs web search), which columns compose <span className="font-mono">q</span>, and (below) org{" "}
        <span className="font-mono">gl</span>/<span className="font-mono">hl</span>/<span className="font-mono">tbs</span>. API key:{" "}
        <Link to={settingsHref} className="text-primary underline hover:no-underline">
          Integrations
        </Link>
        .
      </p>

      <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Serper endpoint (this race)</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Sweeps use this choice. News uses Serper Google News; Web search uses organic web results.
          </p>
        </div>
        <div className="space-y-1.5 max-w-xs">
          <Label className="text-xs">Mode</Label>
          <Select value={draftMode} onValueChange={setDraftMode}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="news">News</SelectItem>
              <SelectItem value="search">Web search</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Query columns</p>
          <p className="text-[11px] text-muted-foreground mt-1">At least one field required. Order in the composed query is fixed (see preview).</p>
        </div>
        <SerperColumnFieldGrid fields={draftFields} onFieldsChange={setDraftFields} />
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" className="text-xs h-8 gap-1" disabled={savingRace || !raceDirty} onClick={saveRaceSerper}>
            {savingRace ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {savingRace ? "Saving…" : "Save Serper race setup"}
          </Button>
          {raceErr && <span className="text-xs text-destructive">{raceErr}</span>}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Search locale (organization)</p>
        {sweepConfigLoading ? (
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Country (gl)</Label>
                <Input
                  className="h-8 text-xs"
                  value={orgGl}
                  onChange={(e) => setOrgGl(e.target.value)}
                  placeholder="us"
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Language (hl)</Label>
                <Input
                  className="h-8 text-xs"
                  value={orgHl}
                  onChange={(e) => setOrgHl(e.target.value)}
                  placeholder="en"
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Time filter (tbs)</Label>
                <Input
                  className="h-8 text-xs"
                  value={orgTbs}
                  onChange={(e) => setOrgTbs(e.target.value)}
                  placeholder="optional"
                  disabled={!isSuperAdmin}
                />
              </div>
            </div>
            {!isSuperAdmin && (
              <p className="text-[11px] text-muted-foreground">
                Only super admins can change org defaults here. Ask an admin, or use{" "}
                <Link to={settingsHref} className="text-primary underline hover:no-underline">
                  Integrations
                </Link>
                .
              </p>
            )}
            {isSuperAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="secondary" size="sm" className="text-xs h-8" disabled={savingOrg} onClick={saveOrgLocale}>
                  {savingOrg ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  {savingOrg ? "Saving…" : "Save locale defaults"}
                </Button>
                {orgErr && <span className="text-xs text-destructive">{orgErr}</span>}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">
              Serper API key: {sweepConfig?.serper_api_key_configured ? "configured" : "not configured"}.
            </p>
          </>
        )}
      </div>

      <div className="rounded-lg border border-border p-3 space-y-2 bg-background/50">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Next sweep (effective)</p>
        <p className="text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">Endpoint:</span> {modeLabel}
        </p>
        <p className="text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">q:</span>{" "}
          <span className="font-mono break-words">{previewQ || "— (add values to selected columns)"}</span>
        </p>
        <p className="text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">gl / hl:</span>{" "}
          <span className="font-mono">
            {effGl} / {effHl}
          </span>
          {draftMode === "search" && effTbs ? (
            <>
              {" "}
              · <span className="font-medium text-foreground">tbs:</span> <span className="font-mono">{effTbs}</span>
            </>
          ) : null}
          {draftMode === "news" && (
            <span className="text-[10px] text-muted-foreground/90"> (news requests use q + page only on the server)</span>
          )}
        </p>
      </div>
    </div>
  );
}
